import { makeStyles, mergeClasses, Spinner, tokens } from '@fluentui/react-components';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useStore } from 'react-redux';

import { OidcAuthHelper } from '../../libs/auth/OidcAuthHelper';
import { AlertType } from '../../libs/models/AlertType';
import { serializeHostContextHeader } from '../../libs/services/hostContextHeader';
import { onMuteMicWhileSpeakingChanged, readMuteMicWhileSpeaking } from '../../libs/voice/voicePreferences';
import { useVoiceSession, VoiceState } from '../../libs/voice/useVoiceSession';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { AppStore, RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { SoundWaveCircle20Filled, SoundWaveCircle20Regular } from '../shared/BundledIcons';

const useClasses = makeStyles({
    button: {
        position: 'absolute',
        right: '54px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground2,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:disabled': {
            cursor: 'default',
            color: tokens.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent',
        },
    },
    active: {
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        '&:hover': {
            backgroundColor: tokens.colorPaletteRedForeground1,
        },
    },
    visuallyHidden: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: 0,
        overflow: 'hidden',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: 'none',
    },
    // Just above the button. Shown, not only announced: the button alone would look like it is connecting anew.
    visibleStatus: {
        position: 'absolute',
        right: '54px',
        bottom: `calc(100% + ${tokens.spacingVerticalXS})`,
        padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
    },
});

// The server's end reasons that are worth telling the user about; the rest end quietly. `text_takeover` (the user
// sent a text question) is one of those: the text answer already shows what happened. `superseded` (a newer voice
// session of the same user) is not, because the other window may be out of sight; nor is `reconnect_superseded`, a
// reconnect the server refused because the user started voice anew elsewhere while it waited.
const endMessages: Record<string, string> = {
    voice_unavailable: 'chatInput.voiceUnavailable',
    flag_off: 'chatInput.voiceUnavailable',
    unavailable: 'chatInput.voiceUnavailable',
    provider_error: 'chatInput.voiceProviderError',
    disconnected: 'chatInput.voiceDisconnected',
    connect_failed: 'chatInput.voiceConnectFailed',
    limit: 'chatInput.voiceLimit',
    max_length: 'chatInput.voiceMaxLength',
    idle: 'chatInput.voiceIdle',
    expired: 'chatInput.voiceExpired',
    rate_limited: 'chatInput.voiceProviderError',
    slow_client: 'chatInput.voiceDisconnected',
    microphone: 'chatInput.voiceMicrophone',
    audio_blocked: 'chatInput.voiceTapToResume',
    human_handover: 'chatInput.voiceHumanHandover',
    chat_unavailable: 'chatInput.voiceChatUnavailable',
    revoked: 'chatInput.voiceRevoked',
    superseded: 'chatInput.voiceSuperseded',
    reconnect_superseded: 'chatInput.voiceSuperseded',
};

// The cancel reasons that leave the user with nothing: no answer spoken, nothing in the chat, and the button still
// listening, so a dropped question looks like a slow one. `not_heard` stays quiet, or background noise would raise
// alerts; `refused` speaks its own refusal; `abandoned` comes with a handover or the session's end, which
// `onEnded` reports.
const cancelMessages: Record<string, string> = {
    not_stored: 'chatInput.voiceQuestionDropped',
    throttled: 'chatInput.voiceQuestionDropped',
    unavailable: 'chatInput.voiceQuestionDropped',
};

// Thinking and speaking keep the listening text, so a turn does not re-announce the state; thinking is announced
// in a region of its own.
const statusKeys: Record<Exclude<VoiceState, 'idle'>, string> = {
    connecting: 'chatInput.voiceConnecting',
    reconnecting: 'chatInput.voiceReconnecting',
    listening: 'chatInput.voiceListening',
    thinking: 'chatInput.voiceListening',
    speaking: 'chatInput.voiceListening',
};

const backendUrl = process.env.REACT_APP_BACKEND_URI ?? window.origin;

interface VoiceButtonProps {
    chatId: string;
    /** While a dictation holds the microphone. A conversation already running can always be ended. */
    disabled?: boolean;
    onActiveChange?: (active: boolean) => void;
}

/** Voice conversation toggle (#2579): talks to /voiceHub; the conversation shows up as ordinary chat bubbles. */
export const VoiceButton: React.FC<VoiceButtonProps> = ({ chatId, disabled = false, onActiveChange }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const store = useStore() as AppStore;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);

    // Own keys only: a plain lookup of `constructor` or `toString` returns an Object.prototype member, which t()
    // calls as a selector and turns into an empty alert or a throw.
    const warn = (messages: Record<string, string>, reason: string) => {
        if (Object.hasOwn(messages, reason)) {
            dispatch(addAlert({ message: t(messages[reason]), type: AlertType.Warning }));
        }
    };

    // Never signinSilent() here: it sets activeNavigator, and OidcNavigationGuard (App.tsx) then swaps the
    // whole app for a loading screen, which unmounts this session. The app renews the token on its own
    // (automaticSilentRenew) ahead of the server's auth.required.
    const currentToken = () => store.getState().app.authToken ?? undefined;
    // Read when the button mounts, as coming back from the Settings page mounts it anew, and again when Settings in
    // another tab or window changes it. The session reads it at every chunk, so a change applies mid-conversation too.
    const [muteMicWhileSpeaking, setMuteMicWhileSpeaking] = useState(readMuteMicWhileSpeaking);
    useEffect(() => onMuteMicWhileSpeakingChanged(setMuteMicWhileSpeaking), []);

    const { state, level, waiting, question, start, stop } = useVoiceSession({
        chatId,
        backendUrl,
        providerName: OidcAuthHelper.getProviderConfig(store).providerName,
        getToken: currentToken,
        getFreshToken: () => Promise.resolve(currentToken()),
        onEnded: (reason) => {
            warn(endMessages, reason);
        },
        onCancelled: (reason) => {
            warn(cancelMessages, reason);
        },
        muteMicWhileSpeaking,
    });

    const active = state !== 'idle';
    useEffect(() => {
        onActiveChange?.(active);
    }, [active, onActiveChange]);
    // Hidden mid-conversation, the button unmounts while active; without this dictation would stay disabled.
    useEffect(
        () => () => {
            onActiveChange?.(false);
        },
        [onActiveChange],
    );
    const connecting = state === 'connecting' || state === 'reconnecting';
    // Red only once the server is ready: nothing said before that is sent, and a red button while connecting
    // had users start talking at once and lose the start of their question.
    // Thinking too: the microphone stays live, so the user can talk over a slow answer.
    const recording = state === 'listening' || state === 'thinking' || state === 'speaking';
    // So "ended" is announced only after a session, not when the button first appears.
    const [hasRun, setHasRun] = useState(false);
    useEffect(() => {
        if (active) {
            setHasRun(true);
        }
    }, [active]);
    // "Thinking" is said once per question, as a region emptied and refilled is announced again. So it stays up until
    // the wait is over, though a sound on the microphone swaps the spinner back to the microphone, and it does not come
    // back when the same question waits again, for a lookup after a spoken preface ("let me check").
    const [thinkingSaid, setThinkingSaid] = useState<{ question: number; up: boolean }>();
    useEffect(() => {
        setThinkingSaid((said) => {
            if (said?.up) {
                if (!waiting && state !== 'thinking') {
                    return { question: said.question, up: false };
                }
                // A question heard meanwhile (background noise during a lookup) carries the same wait on.
                return said.question === question ? said : { question, up: true };
            }
            return state === 'thinking' && said?.question !== question ? { question, up: true } : said;
        });
    }, [state, waiting, question]);
    const saysThinking = thinkingSaid?.up === true;

    // A label that says what a click does, so no aria-pressed: "End voice mode, pressed" contradicts itself.
    const title = active ? t('chatInput.voiceStop') : t('chatInput.voiceStart');
    const status = state !== 'idle' ? t(statusKeys[state]) : hasRun ? t('chatInput.voiceEnded') : '';
    const scale = state === 'listening' ? 1 + level * 0.25 : 1;

    return (
        <>
            <button
                type="button"
                title={title}
                aria-label={title}
                data-testid="voice-button"
                className={mergeClasses(classes.button, recording && classes.active)}
                disabled={disabled && !active}
                onClick={() => {
                    if (active) {
                        stop();
                    } else {
                        void start(serializeHostContextHeader(hostContext));
                    }
                }}
            >
                {connecting ? (
                    <Spinner size="tiny" aria-hidden />
                ) : state === 'thinking' ? (
                    <Spinner size="tiny" appearance="inverted" aria-hidden />
                ) : recording ? (
                    <SoundWaveCircle20Filled
                        style={{ transform: `scale(${scale.toFixed(2)})`, transition: 'transform 120ms' }}
                    />
                ) : (
                    <SoundWaveCircle20Regular />
                )}
            </button>
            <span
                className={state === 'reconnecting' ? classes.visibleStatus : classes.visuallyHidden}
                aria-live="polite"
                data-testid="voice-status"
            >
                {status}
            </span>
            {/* Its own region: the status keeps one text from listening to speaking, so no answer is announced over
                its own audio. This one says "thinking" once per question, and emptying it is not announced. */}
            <span className={classes.visuallyHidden} aria-live="polite" data-testid="voice-activity">
                {saysThinking ? t('chatInput.voiceThinking') : ''}
            </span>
        </>
    );
};
